import { useEffect, useMemo, useState } from 'react';
import { Loader2, Mail, Plus, Reply, ReplyAll, Send, X } from 'lucide-react';
import { Dialog, DialogContentMaxPriority, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContentMaxPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  useEmailClienteContexto,
  useEmailClienteMensagem,
  useEnviarEmailCliente,
  type EmailClienteContexto,
  type EmailClienteMensagem,
  type TipoEmailCliente,
} from '@/hooks/useEmailCliente';

const BTN_PRIMARIO =
  'inline-flex h-9 items-center gap-2 rounded-lg bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] px-4 text-[12px] font-bold text-white transition-all hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)] disabled:cursor-not-allowed disabled:bg-none disabled:bg-muted disabled:text-muted-foreground';
const BTN_GHOST =
  'inline-flex h-9 items-center gap-2 rounded-lg border border-border px-4 text-[12px] font-semibold text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary';
const MODAL = 'max-h-[92vh] max-w-[720px] gap-0 overflow-y-auto border-0 bg-white p-0 dark:bg-background [&>button:last-child]:bg-transparent [&>button:last-child]:text-white [&>button:last-child]:opacity-80';
const TOPO = 'bg-gradient-to-br from-[#4d0713] via-[#68101e] to-[#8e172b] px-5 pb-3.5 pt-4 pr-12 text-white';
const ROTULO = 'pt-1.5 text-[11px] font-semibold text-muted-foreground';
const EMAIL_OK = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CAIXAS_DA_CASA = ['atendimento@empresarialprime.com.br', 'empresarialprime@empresarialprime.com.br', 'gerencial@empresarialprime.com.br'];

export type ModoEmailCliente =
  | { tipo: 'novo'; pedidoId?: string | null; cnpj?: string | null }
  | { tipo: 'responder'; mensagemId: string; todos: boolean; cnpj?: string | null; pedidoId?: string | null };

type Contato = { nome: string | null; email: string };

const titulo = (s: string) => s.toLowerCase().replace(/(?:^|\s)\S/g, (m) => m.toUpperCase());
const primeiroNome = (s: string | null | undefined) => titulo(String(s || '').trim().split(/\s+/)[0] || '');
const fmtData = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '');

function preencher(texto: string, v: Record<string, string>) {
  return texto.replace(/\{(\w+)\}/g, (_, k: string) => v[k] ?? '');
}

function previaAssunto(tipo: TipoEmailCliente | undefined, ctx: EmailClienteContexto, pfy: string, complemento: string) {
  if (!tipo) return '';
  const cabeca = tipo.assunto ?? complemento.toUpperCase();
  const partes = [cabeca, String(ctx.cliente.razao || '').toUpperCase(), ctx.cliente.cnpj_formatado];
  if (tipo.exigePfy && pfy) partes.push(`PFY ${pfy}`);
  else if (ctx.pedido?.numero) partes.push(`PEDIDO ${ctx.pedido.numero}`);
  if (tipo.assunto && complemento) partes.push(complemento.toUpperCase());
  return partes.filter(Boolean).join(' - ');
}

function Destinatarios({ rotulo, escolhidos, sugeridos, lista = [], onChange }: {
  rotulo: string;
  lista?: Array<{ nome: string | null; email: string; papel?: string | null }>;
  escolhidos: Contato[];
  sugeridos: Array<{ nome: string | null; email: string; papel?: string | null }>;
  onChange: (c: Contato[]) => void;
}) {
  const [digitando, setDigitando] = useState('');
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(false);
  const tem = (e: string) => escolhidos.some((c) => c.email.toLowerCase() === e.toLowerCase());
  const adicionar = () => {
    const e = digitando.trim();
    if (!e) return;
    if (!EMAIL_OK.test(e)) { setErro('E-mail incompleto.'); return; }
    if (!tem(e)) onChange([...escolhidos, { nome: null, email: e }]);
    setDigitando('');
  };
  const busca = digitando.trim().toLowerCase();
  const opcoes = lista.filter((s) => !tem(s.email) && (!busca || `${s.nome ?? ''} ${s.email} ${s.papel ?? ''}`.toLowerCase().includes(busca)));
  return (
    <>
      <span className={ROTULO}>{rotulo}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {escolhidos.map((c) => (
          <span key={c.email} title={c.email} className="inline-flex max-w-[260px] items-center gap-1 rounded-md border border-border bg-muted/60 px-2 py-0.5 text-[12px] text-foreground">
            <span className="truncate">{c.nome ? titulo(c.nome) : c.email}</span>
            <button type="button" aria-label={`Tirar ${c.email}`} onClick={() => onChange(escolhidos.filter((x) => x.email !== c.email))} className="text-muted-foreground hover:text-primary">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {sugeridos.filter((s) => !tem(s.email)).map((s) => (
          <button key={s.email} type="button" title={`${s.email}${s.papel ? ' · ' + s.papel : ''}`} onClick={() => onChange([...escolhidos, { nome: s.nome, email: s.email }])}
            className="inline-flex items-center gap-0.5 rounded-md border border-dashed border-border px-2 py-0.5 text-[12px] text-muted-foreground hover:border-primary/50 hover:text-primary">
            <Plus className="h-3 w-3" />{s.nome ? primeiroNome(s.nome) : s.email}
          </button>
        ))}
        <div className="relative min-w-[120px] flex-1">
          <input value={digitando} onChange={(e) => { setDigitando(e.target.value); setErro(''); setAberto(true); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); adicionar(); } if (e.key === 'Escape') setAberto(false); }}
            onFocus={() => setAberto(true)}
            onBlur={() => { setAberto(false); adicionar(); }}
            autoComplete="off" name={`email-${rotulo}`}
            placeholder={lista.length ? 'supervisor ou outro e-mail' : 'outro e-mail'}
            className="h-6 w-full bg-transparent text-[12px] outline-none placeholder:text-muted-foreground/70" />
          {aberto && opcoes.length > 0 && (
            <div className="absolute left-0 top-7 z-10 max-h-56 w-[300px] overflow-y-auto rounded-lg border border-border bg-white py-1 shadow-lg dark:bg-background">
              <p className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Supervisores</p>
              {opcoes.map((s) => (
                <button key={s.email} type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onChange([...escolhidos, { nome: s.nome, email: s.email }]); setDigitando(''); }}
                  className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-muted/60">
                  <span className="text-[12.5px] text-foreground">{s.nome ? titulo(s.nome) : s.email}</span>
                  <span className="truncate text-[11px] text-muted-foreground">{s.papel ? `${s.papel} · ` : ''}{s.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {erro && <span className="w-full text-[11px] text-primary">{erro}</span>}
      </div>
    </>
  );
}

function contatosDe(lista: EmailClienteMensagem['para']): Contato[] {
  return (lista || []).filter((c) => c?.email).map((c) => ({ nome: c.nome ?? null, email: String(c.email) }));
}

export function EmailClienteDialog({ open, onOpenChange, modo }: { open: boolean; onOpenChange: (o: boolean) => void; modo: ModoEmailCliente }) {
  const { toast } = useToast();
  const resposta = modo.tipo === 'responder';
  const { data: original, isLoading: carregandoOriginal } = useEmailClienteMensagem(open && resposta ? modo.mensagemId : null);
  const alvo = resposta
    ? { pedidoId: modo.pedidoId ?? original?.pedido_id ?? null, cnpj: modo.cnpj ?? original?.cnpj ?? null }
    : { pedidoId: modo.pedidoId ?? null, cnpj: modo.cnpj ?? null };
  const { data: ctx, isLoading, error } = useEmailClienteContexto(alvo, open && (!resposta || !!original));
  const enviar = useEnviarEmailCliente();

  const [tipoCodigo, setTipoCodigo] = useState('');
  const [pfy, setPfy] = useState('');
  const [complemento, setComplemento] = useState('');
  const [para, setPara] = useState<Contato[]>([]);
  const [cc, setCc] = useState<Contato[]>([]);
  const [texto, setTexto] = useState('');
  const [editou, setEditou] = useState(false);
  const [iniciado, setIniciado] = useState(false);

  useEffect(() => {
    if (!open) { setIniciado(false); setEditou(false); setComplemento(''); }
  }, [open]);

  const tipo = ctx?.tipos.find((t) => t.codigo === tipoCodigo);
  const defesa = ctx?.defesas.find((d) => d.pfy === pfy);

  useEffect(() => {
    if (!open || !ctx || iniciado) return;
    if (resposta) {
      if (!original) return;
      const casa = (e: string) => CAIXAS_DA_CASA.includes(e.toLowerCase());
      const remetente: Contato[] = original.de_email ? [{ nome: original.de_nome, email: original.de_email }] : [];
      const base = original.direcao === 'saida' ? contatosDe(original.para) : remetente;
      const todosPara = modo.todos && original.direcao === 'entrada' ? [...base, ...contatosDe(original.para)] : base;
      const unicos = (l: Contato[]) => l.filter((c, i) => !casa(c.email) && l.findIndex((x) => x.email.toLowerCase() === c.email.toLowerCase()) === i);
      const p = unicos(todosPara);
      setPara(p);
      setCc(modo.todos ? unicos(contatosDe(original.cc)).filter((c) => !p.some((x) => x.email.toLowerCase() === c.email.toLowerCase())) : []);
      setTexto(`Olá, ${primeiroNome(p[0]?.nome)}.\n\n\n\nAgradecemos.`.replace('Olá, .', 'Olá.'));
    } else {
      setTipoCodigo(ctx.tipo_sugerido);
      setPfy(ctx.defesas[0]?.pfy ?? '');
      setPara(ctx.para_sugeridos.filter((c) => c.padrao).map((c) => ({ nome: c.nome, email: c.email })));
      setCc(ctx.copia_sugeridos.filter((c) => c.padrao).map((c) => ({ nome: c.nome, email: c.email })));
    }
    setIniciado(true);
  }, [open, ctx, original, iniciado, resposta, modo]);

  useEffect(() => {
    if (resposta || !ctx || !tipo || editou) return;
    setTexto(preencher(tipo.texto, {
      contato: primeiroNome(para[0]?.nome),
      pfy: pfy || '',
      cotacao: defesa?.cotacao || '',
      motivo: defesa?.motivo ? defesa.motivo.charAt(0) + defesa.motivo.slice(1).toLowerCase() : 'conforme o parecer da análise de crédito.',
      razao: ctx.cliente.razao || '',
      cnpj: ctx.cliente.cnpj_formatado,
      pedido: ctx.pedido?.numero || '',
    }).replace('Olá, .', 'Olá.'));
  }, [resposta, ctx, tipo, pfy, defesa, para, editou]);

  const assunto = useMemo(() => {
    if (resposta) return original?.assunto ? (/^re:/i.test(original.assunto) ? original.assunto : `Re: ${original.assunto}`) : '';
    return ctx ? previaAssunto(tipo, ctx, pfy, complemento) : '';
  }, [resposta, original, ctx, tipo, pfy, complemento]);

  const falta = !para.length ? 'Escolha para quem vai.'
    : !resposta && !tipo ? 'Escolha o tipo.'
    : !resposta && tipo?.exigePfy && !pfy ? 'Escolha o PFY da defesa.'
    : !resposta && tipo && !tipo.assunto && !complemento.trim() ? 'Escreva o assunto.'
    : texto.trim().length < 5 ? 'Escreva o texto.' : '';

  const submeter = () => {
    if (falta || !ctx) return;
    enviar.mutate({
      pedido_id: ctx.pedido?.id ?? null,
      cnpj: ctx.cliente.cnpj,
      tipo: resposta ? null : tipoCodigo,
      pfy: !resposta && tipo?.exigePfy ? pfy : null,
      complemento: resposta ? null : complemento.trim() || null,
      para: para.map((c) => c.email),
      cc: cc.map((c) => c.email),
      texto: texto.trim(),
      responder_a: resposta ? modo.mensagemId : null,
    }, {
      onSuccess: (r) => {
        toast({ title: 'E-mail enviado', description: `Para ${r.para}. Já está na timeline do cliente.` });
        onOpenChange(false);
      },
      onError: (e) => toast({ title: 'O e-mail não saiu', description: e instanceof Error ? e.message : 'Tente de novo.', variant: 'destructive' }),
    });
  };

  const carregando = isLoading || (resposta && carregandoOriginal);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentMaxPriority className={MODAL}>
        <div className={TOPO}>
          <DialogTitle className="flex items-center gap-2 text-[15px] font-bold">
            {resposta ? (modo.todos ? <ReplyAll className="h-4 w-4" /> : <Reply className="h-4 w-4" />) : <Mail className="h-4 w-4" />}
            {resposta ? (modo.todos ? 'Responder a todos' : 'Responder') : 'Novo e-mail'}
          </DialogTitle>
          {ctx && (
            <p className="mt-1 truncate text-[12px] text-white/75">
              {ctx.cliente.razao} · {ctx.cliente.cnpj_formatado}{ctx.pedido?.numero ? ` · #P${ctx.pedido.numero}` : ''}
            </p>
          )}
        </div>

        {carregando ? (
          <div className="flex items-center gap-2 px-5 py-10 text-[13px] text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando o cliente…</div>
        ) : error || !ctx ? (
          <p className="px-5 py-10 text-[13px] text-primary">{error instanceof Error ? error.message : 'Não foi possível abrir o e-mail deste cliente.'}</p>
        ) : (
          <div className="flex flex-col gap-3 px-5 py-4">
            <div className="grid grid-cols-[64px_minmax(0,1fr)] items-start gap-x-3 gap-y-2.5">
              {!resposta && (
                <>
                  <span className={ROTULO}>Tipo</span>
                  <div className="flex min-w-0 gap-2">
                    <Select value={tipoCodigo} onValueChange={(v) => { setTipoCodigo(v); setEditou(false); }}>
                      <SelectTrigger className="h-8 flex-1 text-[12.5px]"><SelectValue placeholder="Escolha o tipo" /></SelectTrigger>
                      <SelectContentMaxPriority>
                        {ctx.tipos.map((t) => (
                          <SelectItem key={t.codigo} value={t.codigo}>
                            <span className="text-[12.5px]">{t.rotulo}</span>
                            <span className="ml-2 text-[11px] text-muted-foreground">{t.dica}</span>
                          </SelectItem>
                        ))}
                      </SelectContentMaxPriority>
                    </Select>
                    {tipo?.exigePfy && (
                      ctx.defesas.length ? (
                        <Select value={pfy} onValueChange={(v) => { setPfy(v); setEditou(false); }}>
                          <SelectTrigger className="h-8 flex-1 text-[12.5px]"><SelectValue placeholder="PFY" /></SelectTrigger>
                          <SelectContentMaxPriority>
                            {ctx.defesas.map((d) => (
                              <SelectItem key={d.pfy} value={d.pfy}>
                                <span className="text-[12.5px]">PFY {d.pfy}</span>
                                <span className="ml-2 text-[11px] text-muted-foreground">{fmtData(d.em).split(' ')[0]}{d.cotacao ? ` · cotação ${d.cotacao}` : ''}</span>
                              </SelectItem>
                            ))}
                          </SelectContentMaxPriority>
                        </Select>
                      ) : (
                        <span className="flex-1 pt-1.5 text-[11.5px] text-primary">Nenhuma defesa gravada neste pedido. Registre a defesa na triagem primeiro.</span>
                      )
                    )}
                  </div>
                </>
              )}
              <span className={ROTULO}>De</span>
              <span className="pt-1 text-[12.5px]">{titulo(ctx.remetente.nome)} <span className="text-muted-foreground">· {ctx.remetente.caixa}</span></span>
              <Destinatarios rotulo="Para" escolhidos={para} sugeridos={ctx.para_sugeridos} onChange={(c) => { setPara(c); }} />
              <Destinatarios rotulo="Cópia" escolhidos={cc} sugeridos={ctx.copia_sugeridos} lista={ctx.supervisores ?? []} onChange={setCc} />
              {!resposta && tipo && !tipo.assunto && (
                <>
                  <span className={ROTULO}>Sobre</span>
                  <Input value={complemento} onChange={(e) => setComplemento(e.target.value)} maxLength={120} placeholder="Reunião de alinhamento" className="h-8 text-[12.5px]" />
                </>
              )}
              <span className={ROTULO}>Assunto</span>
              <span className="truncate pt-1.5 text-[11.5px] text-muted-foreground" title={assunto}>{assunto || '—'}</span>
            </div>

            <Textarea value={texto} onChange={(e) => { setTexto(e.target.value); setEditou(true); }} rows={9} className="resize-y text-[13px] leading-relaxed" />

            <div className="flex items-center gap-2.5">
              {ctx.remetente.foto_url
                ? <img src={ctx.remetente.foto_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                : <span className="h-8 w-8 rounded-full border border-border bg-muted" />}
              <div className="leading-tight">
                <p className="text-[12.5px] font-semibold">{titulo(ctx.remetente.nome)}</p>
                <p className="text-[11px] text-muted-foreground">assinatura do cadastro · a mesma dos disparos</p>
              </div>
            </div>

            {resposta && original && (
              <details className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                <summary className="cursor-pointer text-[11.5px] font-semibold text-muted-foreground">
                  Mensagem original · {original.de_nome || original.de_email} · {fmtData(original.quando)}
                </summary>
                <p className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap text-[12px] text-muted-foreground">{original.corpo_texto}</p>
              </details>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
              {falta && <span className="mr-auto text-[11.5px] text-muted-foreground">{falta}</span>}
              <button type="button" className={BTN_GHOST} onClick={() => onOpenChange(false)}>Cancelar</button>
              <button type="button" className={cn(BTN_PRIMARIO)} disabled={!!falta || enviar.isPending} onClick={submeter}>
                {enviar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Enviar
              </button>
            </div>
          </div>
        )}
      </DialogContentMaxPriority>
    </Dialog>
  );
}

export function EmailLeituraDialog({ mensagemId, open, onOpenChange, onResponder }: {
  mensagemId: string | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onResponder: (todos: boolean) => void;
}) {
  const { data: m, isLoading, error } = useEmailClienteMensagem(open ? mensagemId : null);
  const lista = (l: EmailClienteMensagem['para']) => (l || []).map((c) => c?.nome || c?.email).filter(Boolean).join(', ');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentMaxPriority className={MODAL}>
        <div className={TOPO}>
          <DialogTitle className="text-[15px] font-bold leading-snug">{m?.assunto || 'E-mail'}</DialogTitle>
          {m && (
            <div className="mt-1.5 space-y-0.5 text-[12px] text-white/75">
              <p><b className="font-semibold text-white">{m.de_nome || m.de_email}</b>{m.de_nome && m.de_email ? ` · ${m.de_email}` : ''} · {fmtData(m.quando)}</p>
              <p className="truncate">para {lista(m.para) || '—'}{m.cc?.length ? ` · cópia ${lista(m.cc)}` : ''}</p>
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 px-5 py-10 text-[13px] text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Abrindo o e-mail…</div>
        ) : error || !m ? (
          <p className="px-5 py-10 text-[13px] text-primary">{error instanceof Error ? error.message : 'Não foi possível abrir este e-mail.'}</p>
        ) : (
          <div className="flex flex-col gap-3 px-5 py-4">
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed">{m.corpo_texto || '(sem texto)'}</p>
            {Number(m.qtd_anexos || 0) > 0 && <p className="text-[11.5px] text-muted-foreground">{m.qtd_anexos} anexo(s) · abra pela caixa {m.caixa}</p>}
            <div className="flex justify-end gap-2 border-t border-border pt-3">
              <button type="button" className={BTN_GHOST} onClick={() => onResponder(true)}><ReplyAll className="h-3.5 w-3.5" /> Responder a todos</button>
              <button type="button" className={BTN_PRIMARIO} onClick={() => onResponder(false)}><Reply className="h-3.5 w-3.5" /> Responder</button>
            </div>
          </div>
        )}
      </DialogContentMaxPriority>
    </Dialog>
  );
}
